// Index same-document entries so cancelled Back/Forward restores the original entry,
// preserving both forward history and the mounted editor rather than replacing its draft.
const POSITION = 'livechatHistoryPosition';
let position = 0;
let href = '';
const guards = new Set<() => boolean>();

function entryPosition(): number | undefined {
	const value: unknown = window.history.state?.[POSITION];
	return typeof value === 'number' && Number.isSafeInteger(value) ? value : undefined;
}

function currentHref(): string {
	return window.location.pathname + window.location.search + window.location.hash;
}

export function writeWorkspaceHistory(destination: string, replace = false): void {
	const previous = entryPosition() ?? position;
	// Tag the starting entry before the first push too.
	if (entryPosition() === undefined)
		window.history.replaceState(
			{ ...window.history.state, [POSITION]: previous },
			'',
			currentHref(),
		);
	position = replace ? previous : previous + 1;
	window.history[replace ? 'replaceState' : 'pushState'](
		{ ...window.history.state, [POSITION]: position },
		'',
		destination,
	);
	href = currentHref();
}

export function registerWorkspaceHistoryGuard(guard: () => boolean): () => void {
	guards.add(guard);
	return () => {
		guards.delete(guard);
	};
}

export function listenWorkspaceHistory(apply: () => void): () => void {
	position = entryPosition() ?? 0;
	href = currentHref();
	window.history.replaceState({ ...window.history.state, [POSITION]: position }, '', href);
	let restoring = false;
	const pop = () => {
		const target = entryPosition();
		if (restoring) {
			if (target === position) restoring = false;
			else if (target !== undefined) window.history.go(position - target);
			return;
		}
		if ([...guards].some((guard) => !guard())) {
			if (target !== undefined && target !== position) {
				restoring = true;
				window.history.go(position - target);
			} else {
				// Old/unindexed entries cannot supply a reliable delta. Keep the editor
				// and restore its URL rather than losing unsaved content.
				window.history.replaceState(
					{ ...window.history.state, [POSITION]: position },
					'',
					href,
				);
			}
			return;
		}
		position = target ?? position + 1;
		href = currentHref();
		if (target === undefined)
			window.history.replaceState(
				{ ...window.history.state, [POSITION]: position },
				'',
				href,
			);
		apply();
	};
	window.addEventListener('popstate', pop);
	return () => window.removeEventListener('popstate', pop);
}
